import type { SignatureContext, SignatureReceipt, SignatureRejection, SignatureSubmission } from "@shared/orderSignature";

export type PendingSignature = {
  schemaVersion: 1;
  kind: "pending";
  actorId: number;
  orderId: string;
  content: SignatureContext;
  submission: SignatureSubmission;
};

export type CompletedSignature = {
  schemaVersion: 1;
  kind: "receipt";
  actorId: number;
  orderId: string;
  receipt: SignatureReceipt;
};

export type SignatureJournalEntry = PendingSignature | CompletedSignature;

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const VERSION = /^[0-9a-f]{64}$/;
const STORAGE_MESSAGE = "此瀏覽器無法安全保存簽收，請保留目前頁面並改用支援保存資料與安全送出的瀏覽器；若已送出，請先查核原筆結果。";

export class SignatureJournalError extends Error {
  constructor(message = STORAGE_MESSAGE) {
    super(message);
    this.name = "SignatureJournalError";
  }
}

function key(actorId: number, orderId: string) {
  if (!Number.isSafeInteger(actorId) || actorId <= 0 || !UUID.test(orderId)) throw new SignatureJournalError();
  return `order-signature:v1:${actorId}:${orderId}`;
}

export function isSignatureContext(value: unknown, orderId: string): value is SignatureContext {
  if (!value || typeof value !== "object") return false;
  const c = value as SignatureContext;
  return c.schemaVersion === 1 && c.orderId === orderId
    && (c.customerId === null || (Number.isSafeInteger(c.customerId) && c.customerId > 0))
    && typeof c.customerName === "string"
    && (c.customerDefaultAddress === null || typeof c.customerDefaultAddress === "string")
    && (c.orderDate === null || typeof c.orderDate === "string")
    && c.shippingMethod === "self_delivery"
    && (c.notes === null || typeof c.notes === "string")
    && Array.isArray(c.items) && c.items.length > 0
    && c.items.every(item => item && Number.isSafeInteger(item.itemId) && item.itemId > 0
      && typeof item.productCode === "string" && typeof item.productName === "string"
      && typeof item.quantity === "string" && item.quantity.trim() !== ""
      && Number.isFinite(Number(item.quantity)) && Number(item.quantity) > 0
      && (item.unit === null || typeof item.unit === "string"));
}

export function isContentVersion(value: unknown): value is string {
  return typeof value === "string" && VERSION.test(value);
}

export function isSignatureReceipt(value: unknown, orderId: string, requestKey?: string, version?: string): value is SignatureReceipt {
  if (!value || typeof value !== "object") return false;
  const r = value as SignatureReceipt;
  return r.success === true && r.orderId === orderId && r.newStatus === "delivered"
    && typeof r.requestKey === "string" && UUID.test(r.requestKey)
    && (!requestKey || r.requestKey === requestKey)
    && Number.isSafeInteger(r.signatureId) && r.signatureId > 0
    && typeof r.signedAt === "string" && Number.isFinite(Date.parse(r.signedAt))
    && isContentVersion(r.contentVersion) && (!version || r.contentVersion === version);
}

function isEntry(value: unknown, actorId: number, orderId: string): value is SignatureJournalEntry {
  if (!value || typeof value !== "object") return false;
  const e = value as SignatureJournalEntry;
  if (e.schemaVersion !== 1 || e.actorId !== actorId || e.orderId !== orderId) return false;
  if (e.kind === "receipt") return isSignatureReceipt(e.receipt, orderId);
  if (e.kind !== "pending" || !isSignatureContext(e.content, orderId)) return false;
  const p = e.submission;
  return !!p && typeof p.requestKey === "string" && UUID.test(p.requestKey)
    && isContentVersion(p.expectedContentVersion)
    && typeof p.signerName === "string" && p.signerName.trim().length > 0 && p.signerName.length <= 100
    && typeof p.signatureData === "string" && /^data:image\/png;base64,/.test(p.signatureData)
    && (p.deviceInfo === undefined || (typeof p.deviceInfo === "string" && p.deviceInfo.length <= 200));
}

export function readSignatureJournal(actorId: number, orderId: string): SignatureJournalEntry | null {
  try {
    const raw = window.localStorage.getItem(key(actorId, orderId));
    if (raw === null) return null;
    const value: unknown = JSON.parse(raw);
    if (!isEntry(value, actorId, orderId)) throw new Error("Invalid journal");
    return value;
  } catch {
    throw new SignatureJournalError("無法讀取已保存的簽收資料，請保留此瀏覽器資料並聯絡管理員；不可重新送出另一筆簽收。");
  }
}

function writeExact(entry: SignatureJournalEntry) {
  try {
    const raw = JSON.stringify(entry);
    const storageKey = key(entry.actorId, entry.orderId);
    window.localStorage.setItem(storageKey, raw);
    if (window.localStorage.getItem(storageKey) !== raw) throw new Error("Journal readback failed");
  } catch {
    throw new SignatureJournalError();
  }
}

/** Caller holds the same actor/order Web Lock until the POST settles. */
export function persistPendingSignature(entry: PendingSignature) {
  if (!isEntry(entry, entry.actorId, entry.orderId)) throw new SignatureJournalError();
  if (readSignatureJournal(entry.actorId, entry.orderId) !== null) {
    throw new SignatureJournalError("此訂單已有待查核的簽收，請重新載入並查核原筆結果，不可覆蓋。");
  }
  writeExact(entry);
}

export function completeSignatureJournal(entry: PendingSignature, receipt: SignatureReceipt) {
  if (!isSignatureReceipt(receipt, entry.orderId, entry.submission.requestKey, entry.submission.expectedContentVersion)) {
    throw new SignatureJournalError("回執與原筆簽收不符，請保留資料並查核送出結果。");
  }
  const current = readSignatureJournal(entry.actorId, entry.orderId);
  if (current?.kind === "receipt" && JSON.stringify(current.receipt) === JSON.stringify(receipt)) return;
  if (JSON.stringify(current) !== JSON.stringify(entry)) {
    throw new SignatureJournalError("已保存的簽收資料有變動，請重新載入查核結果。");
  }
  writeExact({ schemaVersion: 1, kind: "receipt", actorId: entry.actorId, orderId: entry.orderId, receipt });
}

/** Ordinary lookup 404 never permits deleting a pending entry. */
export function isTerminalSignatureRejection(value: unknown, entry: PendingSignature): value is SignatureRejection {
  if (!value || typeof value !== "object") return false;
  const r = value as SignatureRejection;
  return r.success === false && r.terminal === true && r.orderId === entry.orderId
    && r.requestKey === entry.submission.requestKey && typeof r.error === "string"
    && ["ORDER_SIGNATURE_CONTENT_CONFLICT", "ORDER_SIGNATURE_NOT_SIGNABLE"].includes(r.code);
}

export function clearRejectedSignature(entry: PendingSignature, rejection: SignatureRejection) {
  if (!isTerminalSignatureRejection(rejection, entry)) throw new SignatureJournalError("尚未確認原筆簽收已被終局拒絕，不可刪除。");
  if (JSON.stringify(readSignatureJournal(entry.actorId, entry.orderId)) !== JSON.stringify(entry)) {
    throw new SignatureJournalError("已保存的簽收資料有變動，請重新載入查核結果。");
  }
  try {
    const storageKey = key(entry.actorId, entry.orderId);
    // Archive only the minimal terminal response, discarding the image/name/content.
    const archiveKey = `${storageKey}:rejected:${entry.submission.requestKey}`;
    const archived = JSON.stringify({ actorId: entry.actorId, orderId: entry.orderId, rejection });
    const previous = window.localStorage.getItem(archiveKey);
    if (previous !== null && previous !== archived) throw new Error("Rejected receipt changed");
    window.localStorage.setItem(archiveKey, archived);
    if (window.localStorage.getItem(archiveKey) !== archived) throw new Error("Rejection archive failed");
    window.localStorage.removeItem(storageKey);
    if (window.localStorage.getItem(storageKey) !== null) throw new Error("Journal removal failed");
  } catch { throw new SignatureJournalError(); }
}

export async function withSignatureJournalLock<T>(actorId: number, orderId: string, action: () => Promise<T>): Promise<T> {
  if (!navigator.locks?.request) throw new SignatureJournalError();
  return navigator.locks.request(key(actorId, orderId), { mode: "exclusive" }, action);
}
